import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, NgZone, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationExtras,
  Router,
  RouterModule,
} from '@angular/router';
import { filter, take } from 'rxjs';
import { TenantOnboardingService } from '../services/tenant-onboarding.service';
import { environment } from '../../../environments/environment';
import { AuthService, LoginOutcome } from '../../../services/auth.service';
import { API_ERROR_CODES } from '../../shared/constants/api-error-codes';
import { InlineLoadingComponent } from '../../shared/ui/inline-loading.component';
import { PasswordVisibilityToggleComponent } from '../../shared/ui/password-visibility-toggle.component';
import { SecurityCheckModalComponent } from '../../shared/ui/security-check-modal.component';
import { parseShopifyShopInput } from '../../shared/utils/shopify-shop-input.util';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: Record<string, string | number>,
          ) => void;
        };
      };
    };
  }
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    InlineLoadingComponent,
    PasswordVisibilityToggleComponent,
    SecurityCheckModalComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements AfterViewInit, OnInit {
  emailValue = '';
  readonly error = signal<string | null>(null);
  readonly message = signal<string | null>(null);
  readonly loading = signal(false);
  readonly passwordMode = signal(false);
  passwordValue = '';
  readonly passwordError = signal<string | null>(null);
  readonly passwordVisible = signal(false);
  readonly securityCheckOpen = signal(false);
  /** Validated `?shop=` hint forwarded from the embedded Shopify app, propagated to downstream routes. */
  private prefillShop: string | null = null;
  private pendingGoogleCredential: string | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly ngZone: NgZone,
    private readonly onboardingService: TenantOnboardingService,
  ) {}

  ngOnInit(): void {
    const navigationMessage = this.router.getCurrentNavigation()?.extras.state?.['message'];
    const historyMessage = history.state?.message;

    const resolvedMessage =
      (typeof navigationMessage === 'string' && navigationMessage.trim()) ||
      (typeof historyMessage === 'string' && historyMessage.trim()) ||
      null;

    this.message.set(resolvedMessage);

    // `/signup?token=` → `/login?returnUrl=/signup&token=…`: re-persist so refresh / timing cannot drop the invite.
    const qpToken = this.route.snapshot.queryParamMap.get('token')?.trim() ?? '';
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl')?.trim() ?? '';
    if (qpToken.length >= 32) {
      this.authService.persistCustomAppsOnboardingTokenFromQuery(qpToken);
    }
    if (returnUrl === '/signup' && qpToken.length >= 32) {
      this.authService.storePendingSignupReturnAfterLogin();
    }

    // Embedded Shopify app forwards the merchant's *.myshopify.com via `?shop=`; validate and propagate it
    // through downstream tenant / signup routes so the onboarding flow can prefill the store domain field.
    const rawShop = this.route.snapshot.queryParamMap.get('shop')?.trim() ?? '';
    if (rawShop) {
      const parsed = parseShopifyShopInput(rawShop);
      this.prefillShop = parsed?.fullDomain ?? null;
    }
  }

  async ngAfterViewInit(): Promise<void> {
    try {
      await this.loadGoogleIdentityScript();
      this.initGoogleButton();
    } catch {
      this.error.set('Unable to load Google login at the moment.');
    }
  }

  private initGoogleButton(): void {
    const google = window.google;
    const signInContainer = document.getElementById('google-signin-btn');
    if (!google || !signInContainer) {
      this.error.set('Google login is unavailable.');
      return;
    }

    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (response) => {
        this.ngZone.run(() => {
          void this.handleGoogleResponse(response.credential);
        });
      },
    });

    // GIS iframe width is fixed in px — it must not exceed the slot or the right side clips.
    const render = (): void => {
      const slotWidthPx = signInContainer.clientWidth;
      // Keep within slot; allow full 320px when space is available.
      const buttonWidthPx = slotWidthPx > 0 ? Math.min(320, Math.floor(slotWidthPx)) : 320;

      google.accounts.id.renderButton(signInContainer, {
        theme: 'filled_black',
        size: 'large',
        width: buttonWidthPx,
        text: 'continue_with',
        shape: 'pill',
      });
    };

    // First paint can report clientWidth as 0; wait until layout is stable.
    requestAnimationFrame(() => {
      requestAnimationFrame(render);
    });
  }

  private async handleGoogleResponse(credential: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.message.set(null);

    try {
      const gate = await this.authService.verifyGoogleIdentity(credential);
      if ('turnstile_required' in gate) {
        this.pendingGoogleCredential = credential;
        this.securityCheckOpen.set(true);
        return;
      }
      await this.navigateAfterLogin(gate);
    } catch (error) {
      this.error.set(this.authService.resolveAuthError(error));
    } finally {
      this.loading.set(false);
    }
  }

  async onTurnstileVerified(token: string): Promise<void> {
    const credential = this.pendingGoogleCredential;
    if (!credential) {
      this.securityCheckOpen.set(false);
      this.error.set('Security verification failed. Please try again.');
      return;
    }
    this.securityCheckOpen.set(false);
    this.loading.set(true);
    this.error.set(null);
    try {
      const outcome = await this.authService.completeGoogleLogin(credential, token);
      this.pendingGoogleCredential = null;
      await this.navigateAfterLogin(outcome);
    } catch (error) {
      this.error.set(this.authService.resolveAuthError(error));
      if (this.isTurnstileRejection(error)) {
        this.securityCheckOpen.set(true);
        return;
      }
      this.pendingGoogleCredential = null;
    } finally {
      this.loading.set(false);
    }
  }

  onSecurityCheckCancelled(): void {
    this.securityCheckOpen.set(false);
    this.pendingGoogleCredential = null;
    this.error.set('Security verification is required to continue.');
  }

  private isTurnstileRejection(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.error?.code === API_ERROR_CODES.TURNSTILE_VERIFICATION_FAILED
    );
  }

  private async navigateAfterLogin(outcome: LoginOutcome): Promise<void> {
    const pendingMerchantInviteSignup =
      this.authService.hasPendingSignupReturnAfterLogin();

    if (pendingMerchantInviteSignup && outcome.action !== 'signup') {
      this.authService.clearCustomAppsMerchantInviteFlow();
      this.authService.clearSession();
      this.error.set(
        'This merchant signup link cannot be used with an existing LaunchPocket account. Sign in with the store owner Google account that has not already been registered, or ask your admin for a new invite.',
      );
      return;
    }

    if (outcome.action === 'authenticated') {
      if (this.authService.consumePendingSignupReturnAfterLogin()) {
        const extras = this.authService.signupNavigateExtrasWhenCustomAppsPending();
        await this.router.navigate(
          ['/signup'],
          this.withShopQueryParams(extras),
        );
        return;
      }
      if (outcome.type === 'tenant') {
        this.onboardingService.clearCache();
        await this.router.navigate(
          ['/t', outcome.slug],
          this.withShopQueryParams({ replaceUrl: true }),
        );
        return;
      }

      await this.router.navigate(['/platform'], { replaceUrl: true });
      return;
    }

    if (outcome.action === 'tenant_select') {
      this.onboardingService.clearCache();
      await this.router.navigate(['/tenant/select'], {
        ...this.withShopQueryParams({ replaceUrl: true }),
        state: { tenants: outcome.tenants },
      });
      return;
    }

    this.authService.consumePendingSignupReturnAfterLogin();
    const extras = this.authService.signupNavigateExtrasWhenCustomAppsPending();
    await this.router.navigate(['/signup'], {
      ...this.withShopQueryParams(extras),
      state: { email: outcome.email, name: outcome.name },
    });
  }

  /** Adds the validated `?shop=` hint to navigation extras, preserving replaceUrl/state/queryParamsHandling etc. */
  private withShopQueryParams(extras: NavigationExtras = {}): NavigationExtras {
    if (!this.prefillShop) return extras;
    const existing = extras.queryParams;
    const base =
      existing !== null &&
      existing !== undefined &&
      typeof existing === 'object' &&
      !Array.isArray(existing)
        ? { ...existing }
        : {};
    return {
      ...extras,
      queryParams: { ...base, shop: this.prefillShop },
    };
  }

  togglePasswordVisibility(): void {
    this.passwordVisible.update((v) => !v);
  }

  async submitEmail(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.message.set(null);
    this.passwordError.set(null);

    try {
      const email = (this.emailValue ?? '').trim();
      if (!email) {
        this.error.set('Email is required.');
        return;
      }

      this.passwordError.set(null);
      this.passwordValue = '';
      this.passwordVisible.set(false);
      this.passwordMode.set(true);
    } catch (error) {
      this.error.set(this.authService.resolveAuthError(error));
    } finally {
      this.loading.set(false);
    }
  }

  async sendSignupLink(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.message.set(null);
    this.passwordError.set(null);

    try {
      const email = (this.emailValue ?? '').trim();
      if (!email) {
        this.error.set('Email is required.');
        return;
      }

      await this.authService.sendSignupMagicLink(email);
      this.message.set(
        `If this email can be used for signup, a link has been sent to ${email}. Open your inbox to continue. If you don’t see it in 1–2 minutes, check Spam/Promotions.`,
      );
    } catch (error) {
      this.error.set(this.authService.resolveAuthError(error));
    } finally {
      this.loading.set(false);
    }
  }

  async submitPassword(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.passwordError.set(null);

    try {
      const email = (this.emailValue ?? '').trim();
      const password = (this.passwordValue ?? '').trim();
      if (!email || !password) {
        this.passwordError.set('Email and password are required.');
        return;
      }

      const outcome = await this.authService.loginWithPassword(
        email,
        password,
      );
      this.passwordValue = '';
      await this.navigateAfterLogin(outcome);
    } catch (err) {
      this.passwordError.set(this.authService.resolveAuthError(err));
    } finally {
      this.loading.set(false);
    }
  }

  changeEmail(): void {
    this.passwordMode.set(false);
    this.passwordValue = '';
    this.passwordVisible.set(false);
    this.passwordError.set(null);
    this.error.set(null);
    this.message.set(null);
  }

  private loadGoogleIdentityScript(): Promise<void> {
    if (window.google?.accounts?.id) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google script'));
      document.head.appendChild(script);
    });
  }

  goBack(): void {
    const fallback = (): void => {
      void this.router.navigate(['/'], { replaceUrl: true });
    };

    if (window.history.length <= 1) {
      fallback();
      return;
    }

    const currentUrl = this.router.url;
    let gotNavigationAway = false;

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => {
        gotNavigationAway = true;
      });

    window.history.back();

    // If back leads to a guarded route that bounces back to /login (or no navigation happens),
    // fall back to home so the user isn't stuck.
    window.setTimeout(() => {
      if (!gotNavigationAway || this.router.url === currentUrl) {
        fallback();
      }
    }, 350);
  }
}
